import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Divider,
  Alert,
  Table,
  Tag,
  Modal
} from 'antd';
import {
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  HistoryOutlined,
  SecurityScanOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import type { User } from '../../../types/user';
import { UserService, userUtils } from '../../../services/userService';
import { ValidationRules } from '../../../types/user';

const { Title, Text } = Typography;
const { Password } = Input;

interface SecurityTabProps {
  user: User | null;
  mode: 'create' | 'edit' | 'view';
  onChange: (data: Partial<User>) => void;
}

interface SessionRecord {
  id: number;
  loginTime: string;
  logoutTime?: string;
  ipAddress: string;
  userAgent: string;
  isActive: boolean;
  location?: string;
}

const SecurityTab: React.FC<SecurityTabProps> = ({
  user,
  mode,
  onChange
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const isReadOnly = mode === 'view';
  const canChangePassword = mode === 'edit' && user?.userId;

  // Load user sessions for edit/view mode
  useEffect(() => {
    if (mode !== 'create' && user?.userId) {
      loadUserSessions();
    }
  }, [mode, user?.userId]);

  const loadUserSessions = async () => {
    if (!user?.userId) return;

    setLoadingSessions(true);
    try {
      // This would be an actual API call
      // const sessions = await UserService.getUserSessions(user.userId);
      
      // Mock data for demonstration
      const mockSessions: SessionRecord[] = [
        {
          id: 1,
          loginTime: '2025-07-03T10:30:00Z',
          logoutTime: '2025-07-03T18:30:00Z',
          ipAddress: '192.168.1.100',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          isActive: false,
          location: '台北市',
        },
        {
          id: 2,
          loginTime: '2025-07-03T09:00:00Z',
          ipAddress: '192.168.1.105',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          isActive: true,
          location: '台北市',
        },
      ];
      
      setSessions(mockSessions);
    } catch (error) {
      console.error('載入登入記錄失敗:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (values: any) => {
    if (!user?.userId || !canChangePassword) return;

    setLoading(true);
    try {
      await UserService.resetUserPassword(user.userId, {
        newPassword: values.newPassword,
      });
      
      message.success('密碼更新成功');
      form.resetFields();
    } catch (error) {
      message.error(`密碼更新失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle force logout session
  const handleForceLogout = (sessionId: number) => {
    Modal.confirm({
      title: '強制登出確認',
      icon: <ExclamationCircleOutlined />,
      content: '確定要強制登出此會話嗎？使用者將需要重新登入。',
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        try {
          // await UserService.forceLogoutSession(sessionId);
          message.success('已強制登出該會話');
          loadUserSessions();
        } catch (error) {
          message.error(`強制登出失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  // Sessions table columns
  const sessionColumns: ColumnsType<SessionRecord> = [
    {
      title: '登入時間',
      dataIndex: 'loginTime',
      key: 'loginTime',
      width: 160,
      render: (time: string) => new Date(time).toLocaleString('zh-TW'),
    },
    {
      title: '登出時間',
      dataIndex: 'logoutTime',
      key: 'logoutTime',
      width: 160,
      render: (time?: string) => time ? new Date(time).toLocaleString('zh-TW') : '-',
    },
    {
      title: 'IP 位址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 120,
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
      width: 100,
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '線上' : '已登出'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        record.isActive && !isReadOnly ? (
          <Button
            type="link"
            danger
            size="small"
            onClick={() => handleForceLogout(record.id)}
          >
            強制登出
          </Button>
        ) : null
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Space align="center">
            <LockOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>安全設定</Title>
              <Text type="secondary">管理密碼和登入安全</Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* Password Change Section */}
      {canChangePassword && (
        <Card 
          title={
            <Space>
              <LockOutlined style={{ color: '#fa8c16' }} />
              <span>密碼管理</span>
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <Alert
            message="密碼安全要求"
            description="新密碼必須至少8位，包含大小寫字母和數字。"
            type="info"
            style={{ marginBottom: '24px' }}
            showIcon
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handlePasswordChange}
            disabled={loading}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="newPassword"
                  label="新密碼"
                  rules={[
                    { required: true, message: '請輸入新密碼' },
                    { 
                      pattern: ValidationRules.password.pattern,
                      message: ValidationRules.password.message
                    }
                  ]}
                >
                  <Password
                    placeholder="輸入新密碼"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="confirmPassword"
                  label="確認密碼"
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: '請確認新密碼' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('兩次輸入的密碼不一致'));
                      },
                    }),
                  ]}
                >
                  <Password
                    placeholder="再次輸入新密碼"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    icon={<LockOutlined />}
                  >
                    更新密碼
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      )}

      {/* Create Mode Password Info */}
      {mode === 'create' && (
        <Card 
          title={
            <Space>
              <LockOutlined style={{ color: '#52c41a' }} />
              <span>預設密碼</span>
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <Alert
            message="預設密碼資訊"
            description={
              <div>
                <p>新建立的使用者將使用預設密碼：<Text code>Temp123456</Text></p>
                <p>使用者首次登入時會被要求修改密碼。</p>
              </div>
            }
            type="info"
            showIcon
          />
        </Card>
      )}

      {/* Login Sessions Section */}
      {mode !== 'create' && (
        <Card 
          title={
            <Space>
              <HistoryOutlined style={{ color: '#1890ff' }} />
              <span>登入記錄</span>
            </Space>
          }
        >
          <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
            顯示最近的登入會話記錄
          </Text>
          
          <Table
            columns={sessionColumns}
            dataSource={sessions}
            rowKey="id"
            loading={loadingSessions}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: false,
            }}
            size="small"
          />
        </Card>
      )}

      {/* Security Statistics */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#52c41a' }}>
              {sessions.filter(s => s.isActive).length}
            </div>
            <div style={{ color: '#8c8c8c' }}>目前線上</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#1890ff' }}>
              {sessions.length}
            </div>
            <div style={{ color: '#8c8c8c' }}>總登入次數</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#fa8c16' }}>
              {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('zh-TW') : 'N/A'}
            </div>
            <div style={{ color: '#8c8c8c' }}>最後登入</div>
          </Card>
        </Col>
      </Row>

      {/* Security Tips */}
      <Card 
        title={
          <Space>
            <SecurityScanOutlined style={{ color: '#722ed1' }} />
            <span>安全建議</span>
          </Space>
        }
        style={{ marginTop: '24px' }}
      >
        <ul style={{ paddingLeft: '20px', margin: 0 }}>
          <li>定期更新密碼，避免使用過於簡單的密碼</li>
          <li>不要在多個系統使用相同密碼</li>
          <li>注意登入環境的安全性，避免在公共場所登入</li>
          <li>發現異常登入記錄時請立即聯絡系統管理員</li>
          <li>離開工作場所時請記得登出系統</li>
        </ul>
      </Card>
    </div>
  );
};

export default SecurityTab;